import React from 'react';
import { Link } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import Logo from '@/components/Icons/Logo';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { DEFAULT_FONT_FAMILY } from '@/constants/config';
import '@/app/styles/brand.css';

const ASSET_ROOT = '/brand-assets';
const LOGOS = [
  { id: 'black', name: 'Black', color: '#040404', background: '#F5F5F5', use: 'For white and light backgrounds.' },
  { id: 'white', name: 'White', color: '#FFFFFF', background: '#040404', use: 'For black and dark neutral backgrounds.' },
];
const COLORS = [
  { name: 'Black', hex: '#040404', ink: '#FFFFFF', use: 'Text, marks and primary actions' },
  { name: 'White', hex: '#FFFFFF', ink: '#040404', use: 'Canvas and reversed marks' },
  { name: 'Gray', hex: '#616161', ink: '#FFFFFF', use: 'Secondary text on white' },
  { name: 'Surface', hex: '#F5F5F5', ink: '#040404', use: 'Quiet grouping on white' },
  { name: 'Divider', hex: '#E5E5E5', ink: '#040404', use: 'Subtle separation on white' },
  { name: 'Charcoal', hex: '#1C1C1C', ink: '#FFFFFF', use: 'Surfaces in dark appearance' },
];

function SectionHeading({ number, title, children }) {
  return (
    <div className="gab-brand-section-heading">
      <span className="gab-brand-eyebrow">{number}</span>
      <div>
        <h2>{title}</h2>
        {children && <p>{children}</p>}
      </div>
    </div>
  );
}

function DownloadLink({ id, format, name }) {
  const filename = `gab-ai-mark-${id}.${format}`;
  return (
    <a className="gab-brand-download" href={`${ASSET_ROOT}/${filename}`} download={filename} aria-label={`Download ${name.toLowerCase()} Gab AI logo as ${format.toUpperCase()}`}>
      {format.toUpperCase()} <span aria-hidden="true">↓</span>
    </a>
  );
}

export default function BrandPage() {
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);
  return (
    <div
      className="gab-brand"
      style={{
        '--brand-page': isDark ? '#040404' : '#FFFFFF',
        '--brand-ink': isDark ? '#FFFFFF' : '#040404',
        '--brand-copy': isDark ? '#B3B3B3' : '#616161',
        '--brand-rule': isDark ? '#383838' : '#E5E5E5',
        '--brand-card': isDark ? '#1C1C1C' : '#FFFFFF',
        '--brand-accent': isDark ? '#FFFFFF' : '#040404',
        '--brand-action': isDark ? '#FFFFFF' : '#040404',
        '--brand-on-action': isDark ? '#040404' : '#FFFFFF',
        fontFamily: DEFAULT_FONT_FAMILY,
      }}
    >
      <MetaHead
        title="Brand Guidelines & Logos | Gab AI"
        description="Download black and white Gab AI logos and explore our minimal design guidelines for typography, layout, product videos, motion, and sound."
        canonical="/brand"
      />
      <main id="main-content" className="gab-brand-scroll" data-landing-scroll="true">
        <div className="gab-brand-content">
          <header className="gab-brand-hero">
            <div>
              <span className="gab-brand-eyebrow">Gab AI / Brand resources</span>
              <h1>Brand guidelines</h1>
              <p className="gab-brand-lede">Black and white. Clear type. Room to think. A minimal identity for everything we make.</p>
              <a className="gab-brand-primary" href={`${ASSET_ROOT}/gab-ai-logos.zip`} download="gab-ai-logos.zip">
                Download logo pack <span aria-hidden="true">↓</span>
              </a>
              <p className="gab-brand-meta">SVG and transparent PNG · Black and white</p>
            </div>
            <div className="gab-brand-signature" role="img" aria-label="Gab AI logo in white on black">
              <div aria-hidden="true"><Logo width={132} height={132} color="#FFFFFF" /></div>
              <span aria-hidden="true">Gab AI</span>
            </div>
          </header>

          <section className="gab-brand-section" id="logos" aria-labelledby="brand-logos-title">
            <div className="gab-brand-section-heading">
              <span className="gab-brand-eyebrow">01</span>
              <div><h2 id="brand-logos-title">The Gab AI logo</h2><p>Use these files when writing about Gab AI, sharing our work, or linking to the product.</p></div>
            </div>
            <div className="gab-brand-logo-grid">
              {LOGOS.map((logo) => (
                <article className="gab-brand-logo-card" key={logo.id}>
                  <div className="gab-brand-logo-preview" style={{ background: logo.background }} role="img" aria-label={`${logo.name} Gab AI logo`}>
                    <div aria-hidden="true"><Logo width={112} height={112} color={logo.color} /></div>
                  </div>
                  <div className="gab-brand-logo-details">
                    <h3>{logo.name}</h3>
                    <p>{logo.use}</p>
                    <div className="gab-brand-download-row">
                      <DownloadLink id={logo.id} format="svg" name={logo.name} />
                      <DownloadLink id={logo.id} format="png" name={logo.name} />
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <p className="gab-brand-note">SVG scales to any size. PNG files are 1024 × 1024 pixels with a transparent background.</p>
          </section>

          <section className="gab-brand-section">
            <SectionHeading number="02" title="Give the logo room">Keep the original proportions, orientation, and colors so the mark stays recognizable.</SectionHeading>
            <div className="gab-brand-usage-grid">
              <figure className="gab-brand-space-figure">
                <div className="gab-brand-clear-space" role="img" aria-label="Leave clear space equal to one quarter of the logo width on every side">
                  <div className="gab-brand-clear-mark" aria-hidden="true"><Logo width={144} height={144} color="#040404" /></div>
                </div>
                <figcaption>Leave at least ¼ of the mark’s width clear on every side.</figcaption>
              </figure>
              <div className="gab-brand-rules">
                <div><h3>Make it easy to see</h3><p>Choose the version with clear contrast against its background. Keep the mark at least 24 pixels wide on screen.</p></div>
                <div><h3>Keep the artwork intact</h3><p>Use the supplied files. Avoid stretching, rotating, cropping, recoloring, adding effects, or placing the logo over busy imagery.</p></div>
                <div><h3>Represent the relationship accurately</h3><p>Use the logo to identify Gab AI. Don’t combine it with another logo or suggest a partnership or endorsement that hasn’t been agreed.</p></div>
              </div>
            </div>
          </section>

          <section className="gab-brand-section">
            <SectionHeading number="03" title="Our colors">Black, white, and neutral grays. This palette applies to product UI, marketing, native apps, documents, and video. Green is retired as a brand color.</SectionHeading>
            <div className="gab-brand-palette">
              {COLORS.map((color) => (
                <div key={color.name} className="gab-brand-swatch" style={{ background: color.hex, color: color.ink }}>
                  <h3>{color.name}</h3><code>{color.hex}</code><p>{color.use}</p>
                </div>
              ))}
            </div>
            <p className="gab-brand-note">Use black actions with white labels on light surfaces; reverse them in dark appearance. Dark surfaces use #B3B3B3 secondary text and #383838 dividers. Keep body text contrast at 4.5:1 or higher and essential control boundaries at 3:1. Quiet dividers are for decorative separation.</p>
            <p className="gab-brand-note">Keep links, focus, selection, and success clear with neutral styling, icons, and labels. Reserve red or amber for real errors and warnings. Preserve system controls, third-party marks, and meaningful color in content. Avoid decorative gradients, tinted backgrounds, and colored glows.</p>
          </section>

          <section className="gab-brand-section">
            <SectionHeading number="04" title="Type and name">Keep the type familiar and the language direct.</SectionHeading>
            <div className="gab-brand-type-grid">
              <article className="gab-brand-type-card">
                <span className="gab-brand-type-sample" aria-hidden="true">Aa</span>
                <h3>A familiar sans serif</h3>
                <p>Use native system sans serif fonts, with Arial as a practical fallback for documents. Keep one family per piece, readable body copy, and medium or semibold emphasis. Avoid decorative serif accents and crowded headlines.</p>
              </article>
              <article className="gab-brand-type-card">
                <span className="gab-brand-name-sample">Gab AI</span>
                <h3>Two words. Capital G, A, and I.</h3>
                <p>Write “Gab AI” in text and use <a href="https://gab.ai">gab.ai</a> for our web address. Our short brand message is <strong>AI that works.</strong> Explain the task and result in plain language.</p>
              </article>
            </div>
            <div className="gab-brand-description">
              <span className="gab-brand-eyebrow">Describing Gab AI</span>
              <p>Gab AI brings AI chat and useful work into one workspace. Explore ideas with different models, research a question, and create documents, presentations, spreadsheets, media, and code.</p>
            </div>
          </section>

          <section className="gab-brand-section" id="design">
            <SectionHeading number="05" title="Design with purpose">Use Apple-style restraint while keeping Gab AI’s own identity. Make the user’s work the focus.</SectionHeading>
            <div className="gab-brand-type-grid">
              <article className="gab-brand-type-card">
                <h3>Space creates hierarchy</h3>
                <p>Use a consistent 4/8-point spacing rhythm, aligned edges, and one primary action per decision. Group with space before adding cards. Keep corners restrained and shadows subtle.</p>
              </article>
              <article className="gab-brand-type-card">
                <h3>Make every detail useful</h3>
                <p>Show real product output and a clear next step. Keep controls familiar, focus visible, and layouts readable at narrow widths and larger text sizes. Remove decoration that competes with the task.</p>
              </article>
            </div>
          </section>

          <section className="gab-brand-section" id="motion">
            <SectionHeading number="06" title="Product videos, motion, and sound">Plan the story and still frames before animation. Keep the product and its result easy to understand.</SectionHeading>
            <div className="gab-brand-type-grid">
              <article className="gab-brand-type-card">
                <h3>One idea per scene</h3>
                <p>Give the subject space and time to read. Use controlled easing, connect related scenes, and vary the pace around the message. Cut when the subject changes. Skip constant zooms and decorative effects.</p>
              </article>
              <article className="gab-brand-type-card">
                <h3>Leave room to listen</h3>
                <p>Choose sparse music and purposeful sound effects. Keep narration clear and caption it. Review paused frames and silent playback. Respect reduced-motion preferences in the interface.</p>
              </article>
            </div>
            <p className="gab-brand-note">Design reference: <a href="https://x.com/leomeethewoo/status/2103529310208606701">Leo’s Apple Framework for product videos</a>. These are Gab AI’s adaptations, not an official Apple specification.</p>
          </section>

          <section className="gab-brand-section" id="native-setup">
            <SectionHeading number="07" title="Native setup and permissions">Use a clean, minimal Apple-style design for Mac setup windows, permission guides, and floating instruction cards.</SectionHeading>
            <figure className="gab-brand-native-example">
              <img src={`${ASSET_ROOT}/mac-permission-guide.png`} width="1440" height="612" loading="lazy"
                alt="Example Gab AI permission guide: a white rounded card with a black Gab logo, gray instructions, and a charcoal Open System Settings button." />
              <figcaption>A native permission guide. Keep the next action easy to find.</figcaption>
            </figure>
            <div className="gab-brand-type-grid">
              <article className="gab-brand-type-card">
                <h3>White, neutral, and quiet</h3>
                <p>Use white surfaces, a black Gab mark, charcoal text and buttons, subtle gray borders, and generous spacing. Avoid green backgrounds, green buttons, mint tints, gradients, and decorative brand treatments in these guides.</p>
              </article>
              <article className="gab-brand-type-card">
                <h3>Familiar controls, clear instructions</h3>
                <p>Use native system type, restrained rounded corners, one clear primary action, and a quiet cancel action. Name the exact macOS control, keep the guide visible beside System Settings, and show completion only after permission is detected.</p>
              </article>
            </div>
          </section>

          <aside className="gab-brand-contact">
            <div><h2>Need something else?</h2><p>Get in touch for a different format or a question about using the brand.</p></div>
            <Link href="/contact" asChild>
              <a className="gab-brand-contact-link">Contact us <span aria-hidden="true">↗</span></a>
            </Link>
          </aside>
        </div>
        <PublicFooter variant="editorial" showHero={false} />
      </main>
    </div>
  );
}
